import React from 'react';
import { Barcode, CreditCard, QrCode } from 'phosphor-react';
import StyledInput from '../inputs/StyledInput';
import usePresence from '../../use/usePresence';
import { BillingType, FORMAS_PAGAMENTO } from '../../types/enums';
import { CamposCheckout } from '../../use/useSubscription';

const ICONES: Record<BillingType, React.ReactNode> = {
  [BillingType.CREDIT_CARD]: <CreditCard className="w-4 h-4" />,
  [BillingType.PIX]: <QrCode className="w-4 h-4" />,
  [BillingType.BOLETO]: <Barcode className="w-4 h-4" />,
};

/** Uma linha por forma de pagamento, sob a explicação do que acontece ao confirmar. */
const AVISOS: Record<BillingType, string> = {
  [BillingType.CREDIT_CARD]: 'Aprovação na hora e renovação automática a cada ciclo.',
  [BillingType.PIX]: 'Geramos o QR Code aqui mesmo. Costuma cair em minutos.',
  [BillingType.BOLETO]: 'Geramos a linha digitável aqui mesmo. Compensa em até 3 dias úteis.',
};

interface CheckoutFormProps {
  forma: BillingType;
  setForma: (forma: BillingType) => void;
  campos: CamposCheckout;
  setCampo: (campo: keyof CamposCheckout, valor: string) => void;
  desabilitado: boolean;
}

/**
 * Formulário do checkout transparente: escolha da forma de pagamento e, no cartão,
 * os campos que o Asaas exige.
 *
 * <p>Nada aqui é persistido. Os valores vivem no estado do `useSubscription`, vão
 * uma única vez ao backend a caminho do Asaas e são zerados no envio.</p>
 */
export default function CheckoutForm({
  forma,
  setForma,
  campos,
  setCampo,
  desabilitado,
}: CheckoutFormProps) {
  const cartao = forma === BillingType.CREDIT_CARD;
  // Os seis campos do cartão entravam e saíam no frame ao trocar de forma.
  const camposCartao = usePresence(cartao, 180);

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Como quer pagar</h2>

      <div role="tablist" aria-label="Forma de pagamento" className="grid grid-cols-3 gap-2">
        {Object.values(BillingType).map((tipo) => (
          <button
            key={tipo}
            type="button"
            role="tab"
            aria-selected={forma === tipo}
            disabled={desabilitado}
            onClick={() => setForma(tipo)}
            className={`flex flex-col items-center gap-1.5 py-3 rounded-2xl border-2 text-xs font-semibold transition-colors duration-200 ease-soft active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100 ${
              forma === tipo
                ? 'border-blush-400 bg-blush-50 dark:bg-blush-900/20 text-blush-600 dark:text-blush-300'
                : 'border-nude-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-nude-500 dark:text-gray-400 hover:border-blush-200'
            }`}
          >
            {ICONES[tipo]}
            {FORMAS_PAGAMENTO[tipo]}
          </button>
        ))}
      </div>

      <p className="text-[11px] text-nude-400 dark:text-gray-500 -mt-1">{AVISOS[forma]}</p>

      <StyledInput
        label="CPF ou CNPJ"
        name="cpfCnpj"
        value={campos.cpfCnpj}
        onChange={(e) => setCampo('cpfCnpj', e.target.value)}
        placeholder="000.000.000-00"
        disabled={desabilitado}
      />

      {camposCartao.montado && (
        <div
          data-state={camposCartao.estado}
          className="flex flex-col gap-4 data-[state=open]:animate-enter data-[state=closed]:animate-exit"
        >
          <StyledInput
            label="Número do cartão"
            name="numeroCartao"
            type="tel"
            value={campos.numeroCartao}
            onChange={(e) => setCampo('numeroCartao', e.target.value)}
            placeholder="0000 0000 0000 0000"
            disabled={desabilitado}
            icon={<CreditCard className="w-4 h-4" />}
          />

          <StyledInput
            label="Nome impresso no cartão"
            name="nomeCartao"
            value={campos.nomeCartao}
            onChange={(e) => setCampo('nomeCartao', e.target.value)}
            placeholder="MARIA S SILVA"
            disabled={desabilitado}
          />

          <div className="grid grid-cols-2 gap-3">
            <StyledInput
              label="Validade"
              name="validade"
              type="tel"
              value={campos.validade}
              onChange={(e) => setCampo('validade', e.target.value)}
              placeholder="MM/AA"
              disabled={desabilitado}
            />
            <StyledInput
              label="CVV"
              name="cvv"
              type="tel"
              value={campos.cvv}
              onChange={(e) => setCampo('cvv', e.target.value)}
              placeholder="000"
              disabled={desabilitado}
            />
          </div>

          {/* Endereço e telefone do titular: exigidos pelo antifraude do Asaas, não por nós. */}
          <div className="grid grid-cols-2 gap-3">
            <StyledInput
              label="CEP"
              name="cep"
              type="tel"
              value={campos.cep}
              onChange={(e) => setCampo('cep', e.target.value)}
              placeholder="00000-000"
              disabled={desabilitado}
            />
            <StyledInput
              label="Número"
              name="enderecoNumero"
              value={campos.enderecoNumero}
              onChange={(e) => setCampo('enderecoNumero', e.target.value)}
              placeholder="123"
              disabled={desabilitado}
            />
          </div>

          <StyledInput
            label="Telefone"
            name="telefone"
            type="tel"
            value={campos.telefone}
            onChange={(e) => setCampo('telefone', e.target.value)}
            placeholder="(00) 00000-0000"
            disabled={desabilitado}
          />
        </div>
      )}
    </div>
  );
}
